import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import DocumentPrintCard from "../components/DocumentPrintCard";
import { DocumentAPI } from "../lib/api";
import { useCenturion } from "../context/CenturionContext";
import { toast } from "sonner";
import { ArrowLeft, Printer, FileDown, Trash2, Link2, Lock, RefreshCw } from "lucide-react";

const DocumentViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { unlocked } = useCenturion();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const load = async () => {
    try {
      const data = await DocumentAPI.get(id);
      setDoc(data);
    } catch {
      toast.error("Document not found");
      setDoc(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable react-hooks/exhaustive-deps */ }, [id]);

  const handlePrint = () => {
    document.title = `${doc?.title || "Document"} — ${doc?.issuer?.company || ""}`;
    window.print();
  };

  const handleDelete = async () => {
    try {
      await DocumentAPI.remove(id);
      toast.success("Document deleted");
      navigate("/documents");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await DocumentAPI.generate(id);
      toast.success("Regenerating…");
      await load();
    } catch {
      toast.error("Regenerate failed");
    } finally {
      setRegenerating(false);
    }
  };

  const copyVerify = () => {
    const url = `${window.location.origin}/verify-doc/${doc.verification_id}`;
    navigator.clipboard.writeText(url);
    toast.success("Verification URL copied");
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-6 py-12 animate-pulse">
          <div className="h-6 w-40 bg-white/5 rounded mb-6" />
          <div className="bg-white/5 h-[600px] rounded-lg" />
        </div>
      </Layout>
    );
  }

  if (!doc) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h2 className="font-display text-[40px] font-semibold">Document not found</h2>
          <Link to="/documents" className="btn-primary mt-8 inline-flex">Back to documents</Link>
        </div>
      </Layout>
    );
  }

  const verificationUrl = `${window.location.origin}/verify-doc/${doc.verification_id}`;

  return (
    <Layout>
      <div className="no-print sticky top-[68px] z-20 bg-[#07060a]/80 backdrop-blur border-b border-[rgba(212,165,75,0.1)]">
        <div className="max-w-5xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <Link to="/documents" className="text-[13px] text-white/60 hover:text-white flex items-center gap-1.5">
            <ArrowLeft size={14} /> Back to documents
          </Link>
          <div className="flex items-center gap-2">
            <span className={`status-badge ${doc.status === "final" ? "paid" : "draft"}`}>{doc.status}</span>
            {doc.public_verify && (
              <button onClick={copyVerify} className="btn-ghost"><Link2 size={14} /> Verify URL</button>
            )}
            <button onClick={handlePrint} className="btn-ghost"><Printer size={14} /> Print</button>
            <button onClick={handlePrint} className="btn-ghost"><FileDown size={14} /> PDF</button>
            {unlocked ? (
              <>
                <button onClick={handleRegenerate} disabled={regenerating} className="btn-ghost">
                  <RefreshCw size={14} className={regenerating ? "animate-spin" : ""} /> Regenerate
                </button>
                <button onClick={() => setConfirmDelete(true)} className="btn-danger"><Trash2 size={14} /> Delete</button>
              </>
            ) : (
              <span className="text-[11px] text-white/45 flex items-center gap-1.5"><Lock size={12} /> Issuer locked</span>
            )}
          </div>
        </div>
      </div>

      {doc.generation?.state === "pending" && (
        <div className="no-print max-w-5xl mx-auto px-6 pt-6">
          <div className="glass p-4 flex items-center gap-3">
            <RefreshCw size={16} className="animate-spin text-[#d4a54b]" />
            <span className="text-[13px] text-white/70">Still drafting — refresh in a moment.</span>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 py-12">
        <DocumentPrintCard document={doc} verificationUrl={verificationUrl} />
      </div>

      {confirmDelete && (
        <div className="no-print fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="glass max-w-md w-full mx-4 p-6">
            <h3 className="text-[18px] font-semibold mb-2">Delete document?</h3>
            <p className="text-white/60 text-[13px]">
              You're about to delete <span className="text-white">{doc.title}</span>. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button className="btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete}><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default DocumentViewPage;
